import { createBrowserRouter, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { App } from './App';
import { AuthGuard } from './components/shell/AuthGuard';
import { useFeatureFlag } from './stores/featureFlagsStore';

const LandingPage = lazy(() => import('./panels/landing/LandingPage').then(m => ({ default: m.LandingPage })));
const AuthPage = lazy(() => import('./panels/auth/AuthPage').then(m => ({ default: m.AuthPage })));
const ResetPasswordPage = lazy(() => import('./panels/auth/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })));
const DashboardPanel = lazy(() => import('./panels/dashboard/DashboardPanel').then(m => ({ default: m.DashboardPanel })));
const DashboardCommandPanel = lazy(() => import('./panels/dashboard/DashboardCommandPanel'));
const ZilmPanel = lazy(() => import('./panels/zilm/ZilmPanel').then(m => ({ default: m.ZilmPanel })));
const FundingPanel = lazy(() => import('./panels/funding/FundingPanel').then(m => ({ default: m.FundingPanel })));
const CompliancePanel = lazy(() => import('./panels/compliance/CompliancePanel').then(m => ({ default: m.CompliancePanel })));
const AnalyticsPanel = lazy(() => import('./panels/analytics/AnalyticsPanel').then(m => ({ default: m.AnalyticsPanel })));
const WorkflowsPanel = lazy(() => import('./panels/workflows/WorkflowsPanel').then(m => ({ default: m.WorkflowsPanel })));
const AssessmentsPanel = lazy(() => import('./panels/assessments/AssessmentsPanel').then(m => ({ default: m.AssessmentsPanel })));
const BriefsPanel = lazy(() => import('./panels/briefs/BriefsPanel').then(m => ({ default: m.BriefsPanel })));
const AdminPanel = lazy(() => import('./panels/admin/AdminPanel').then(m => ({ default: m.AdminPanel })));
const ToolsPanel = lazy(() => import('./panels/tools/ToolsPanel').then(m => ({ default: m.ToolsPanel })));
const WhatsNewPanel = lazy(() => import('./panels/whatsnew/WhatsNewPanel').then(m => ({ default: m.WhatsNewPanel })));
const SettingsPanel = lazy(() => import('./panels/settings/SettingsPanel').then(m => ({ default: m.SettingsPanel })));
const DataExplorerPanel = lazy(() => import('./panels/data-explorer/DataExplorerPanel').then(m => ({ default: m.DataExplorerPanel })));
const DataPanePage = lazy(() => import('./panels/data-explorer/DataPanePage').then(m => ({ default: m.DataPanePage })));
const NewDataPanePage = lazy(() => import('./panels/data-pane/DataPanePage'));
const FeedDrillPage = lazy(() => import('./panels/data-pane/FeedDrillPage'));
const TableDetailPage = lazy(() => import('./panels/data-pane/TableDetailPage'));
const PlaygroundPage = lazy(() => import('./panels/data-pane/PlaygroundPage'));
const FunctionView = lazy(() => import('./panels/function/FunctionView').then(m => ({ default: m.FunctionView })));
const ToolView = lazy(() => import('./panels/tool/ToolView').then(m => ({ default: m.ToolView })));
const LabsPanel = lazy(() => import('./panels/labs/LabsPanel').then(m => ({ default: m.LabsPanel })));
const VdotMapPanel = lazy(() => import('./panels/vdot-map/VdotMapPanel').then(m => ({ default: m.VdotMapPanel })));

function PanelLoader() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--t3)' }}>
      Loading...
    </div>
  );
}

function SuspensePanel({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PanelLoader />}>{children}</Suspense>;
}

function DataExplorerGate() {
  const enabled = useFeatureFlag('data_pane_v2');
  // `?layout=legacy` is the explicit user opt-in to view the legacy
  // Data Lineage Explorer even when the v2 feature flag is on. The
  // LayoutToggle component on the new Data Pane catalog uses this so
  // users can compare layouts without hitting the auto-redirect.
  const wantsLegacy = new URLSearchParams(window.location.search).get('layout') === 'legacy';
  if (enabled && !wantsLegacy) return <Navigate to="/data-pane" replace />;
  return <SuspensePanel><DataExplorerPanel /></SuspensePanel>;
}

// `/data-explorer/:dataset/:table` is the legacy per-table detail view —
// the only deep link target for clicks from the new MedallionLineage cards
// (and any external bookmarks). It does NOT participate in the data_pane_v2
// gate; redirecting it would send legitimate table-detail navigations back
// to the catalog and prevent users from inspecting an individual table.

export const router = createBrowserRouter([
  // ── Public routes (no auth required) ──
  { path: '/', element: <SuspensePanel><LandingPage /></SuspensePanel> },
  { path: '/auth', element: <SuspensePanel><AuthPage /></SuspensePanel> },
  { path: '/auth/reset', element: <SuspensePanel><ResetPasswordPage /></SuspensePanel> },

  // ── Protected routes (auth required) ──
  {
    element: <AuthGuard />,
    children: [
      {
        element: <App />,
        children: [
          { path: 'dashboard', element: <SuspensePanel><DashboardCommandPanel /></SuspensePanel> },
          { path: 'dashboard/scope/:kind', element: <SuspensePanel><DashboardCommandPanel /></SuspensePanel> },
          { path: 'dashboard/scope/:kind/:state', element: <SuspensePanel><DashboardCommandPanel /></SuspensePanel> },
          { path: 'dashboard/scope/:kind/:state/:county', element: <SuspensePanel><DashboardCommandPanel /></SuspensePanel> },
          { path: 'dashboard/entity/:entityKind/:entityId', element: <SuspensePanel><DashboardCommandPanel /></SuspensePanel> },
          { path: 'dashboard-legacy', element: <SuspensePanel><DashboardPanel /></SuspensePanel> },
          { path: 'zilm', element: <SuspensePanel><ZilmPanel /></SuspensePanel> },
          { path: 'funding', element: <SuspensePanel><FundingPanel /></SuspensePanel> },
          { path: 'compliance', element: <SuspensePanel><CompliancePanel /></SuspensePanel> },
          { path: 'analytics', element: <SuspensePanel><AnalyticsPanel /></SuspensePanel> },
          { path: 'workflows', element: <SuspensePanel><WorkflowsPanel /></SuspensePanel> },
          { path: 'assessments', element: <SuspensePanel><AssessmentsPanel /></SuspensePanel> },
          { path: 'briefs', element: <SuspensePanel><BriefsPanel /></SuspensePanel> },
          { path: 'admin', element: <SuspensePanel><AdminPanel /></SuspensePanel> },
          { path: 'tools', element: <SuspensePanel><ToolsPanel /></SuspensePanel> },
          { path: 'agents', element: <Navigate to="/labs" replace /> },
          { path: 'whatsnew', element: <SuspensePanel><WhatsNewPanel /></SuspensePanel> },
          { path: 'settings', element: <SuspensePanel><SettingsPanel /></SuspensePanel> },
          { path: 'data-explorer', element: <DataExplorerGate /> },
          { path: 'data-explorer/:dataset/:table', element: <SuspensePanel><DataPanePage /></SuspensePanel> },
          { path: 'data-pane', element: <SuspensePanel><NewDataPanePage /></SuspensePanel> },
          { path: 'data-pane/playground', element: <SuspensePanel><PlaygroundPage /></SuspensePanel> },
          { path: 'data-pane/:categoryKey', element: <SuspensePanel><NewDataPanePage /></SuspensePanel> },
          { path: 'data-pane/:categoryKey/:feedId/table/:dataset/:table', element: <SuspensePanel><TableDetailPage /></SuspensePanel> },
          { path: 'data-pane/:categoryKey/:feedId', element: <SuspensePanel><FeedDrillPage /></SuspensePanel> },
          { path: 'functions/:funcKey', element: <SuspensePanel><FunctionView /></SuspensePanel> },
          { path: 'tools/:toolKey', element: <SuspensePanel><ToolView /></SuspensePanel> },
          { path: 'labs', element: <SuspensePanel><LabsPanel /></SuspensePanel> },
          { path: 'maps/vdot', element: <SuspensePanel><VdotMapPanel /></SuspensePanel> },
        ],
      },
    ],
  },

  // Catch-all — redirect unknown routes to landing
  { path: '*', element: <Navigate to="/" replace /> },
]);
